import { ArticleMetricType, FeedbackType, MSALInfo } from '../types';
import {
    InteractionType,
    TrackInteractionMetadata,
    TrackInteractionRequest
} from '../api/usePostTrackInteraction.ts';
import apiPath from '../api/apiPath.ts';
import createAuthenticatedFetch from '../api/createAuthenticatedFetch.ts';

async function trackInteraction(
    pagesUserId: string,
    interactionType: InteractionType,
    kbEntryId: string | null,
    metadata: TrackInteractionMetadata | null,
    interactionId: string | null,
    clientId: string,
    msalInfo: MSALInfo,
    requiresAuthentication: boolean = false
): Promise<string | undefined> {
    try {
        const request: TrackInteractionRequest = {
            interactionType,
            kbEntryId,
            metadata,
            interactionId,
            url: window.location.href
        };

        const authFetch = createAuthenticatedFetch(
            clientId,
            msalInfo,
            requiresAuthentication
        );

        const res = await authFetch(apiPath('/track_interaction'), {
            method: 'POST',
            cache: 'no-cache',
            body: JSON.stringify(request),
            headers: {
                'Content-Type': 'application/json',
                'Pages-User-Id': pagesUserId
            }
        });
        const { interactionId: newInteractionId } = await res.json();
        return newInteractionId;
    } catch {
        // Ignore interaction errors
        return undefined;
    }
}

export async function onGiveArticleFeedback(
    sessionKey: string,
    pagesUserId: string,
    articleId: string,
    type: ArticleMetricType,
    clientId: string,
    msalInfo: MSALInfo,
    requiresAuthentication: boolean = false
): Promise<void> {
    try {
        const authFetch = createAuthenticatedFetch(
            clientId,
            msalInfo,
            requiresAuthentication
        );
        await authFetch(
            apiPath(`article_feedback?articleId=${articleId}&type=${type}`),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Pages-User-Id': pagesUserId
                }
            }
        );

        const event = new CustomEvent('feedbackGiven', {
            detail: { sessionKey }
        });
        window.dispatchEvent(event);
    } catch {
        // Ignore feedback errors
    }
}

export async function recordArticleView(
    pagesUserId: string,
    articleId: string,
    clientId: string,
    msalInfo: MSALInfo,
    requiresAuthentication: boolean = false
): Promise<void> {
    try {
        const authFetch = createAuthenticatedFetch(
            clientId,
            msalInfo,
            requiresAuthentication
        );
        await authFetch(apiPath(`article_view?articleId=${articleId}`), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Pages-User-Id': pagesUserId
            }
        });
    } catch {
        // Ignore view tracking errors
    }
}

export async function onGiveFeedback(
    pagesUserId: string,
    entryId: string,
    feedbackType: FeedbackType,
    score: number,
    searchText: string,
    clientId: string,
    msalInfo: MSALInfo,
    requiresAuthentication: boolean = false
): Promise<void> {
    const sessionKey = `${entryId}-interaction`;
    const previousInteractionId = sessionStorage.getItem(sessionKey);
    const latestSearchInteractionId = sessionStorage.getItem(
        'latestSearchInteractionId'
    );

    const interactionId = await trackInteraction(
        pagesUserId,
        InteractionType.FeedbackClicked,
        entryId,
        {
            feedbackType,
            score,
            searchText,
            searchInteractionId: latestSearchInteractionId
        },
        previousInteractionId,
        clientId,
        msalInfo,
        requiresAuthentication
    );

    if (interactionId) {
        sessionStorage.setItem(sessionKey, interactionId);

        const event = new CustomEvent('feedbackGiven', {
            detail: { sessionKey }
        });
        window.dispatchEvent(event);
    }
}
